import { Box, Inline, Stack, Text } from "@origin-digital/ods-core";
import { formatLabel } from "@/utils";
import React, { memo } from "react";
import { solarBatteryOutlook as content } from "../content.json";
import IconSolar from "@/assets/spot_solar.svg";
import { SolarAccordion } from "./SolarAccordion";
import { IISolarBatteryOutlookResponse } from "@/contract/generated/electrify-v1";
import { StyledHeading } from "@/components";
import { isOutlier } from "../utils";

type SummaryProps = {
  solarBatterySystem: IISolarBatteryOutlookResponse;
};

export const Solar = memo(({ solarBatterySystem }: SummaryProps) => {
  const { size, percentageDailyConsumption } = solarBatterySystem;
  const { testId } = content;
  const accordionId = `${size}-kw-battery-accordion`;

  const isNoCover =
    percentageDailyConsumption && percentageDailyConsumption < 70;
  const isNotEnoughCover =
    percentageDailyConsumption &&
    percentageDailyConsumption >= 70 &&
    percentageDailyConsumption < 100;

  const isOutlierCover = isOutlier({ solarBatterySystem });

  const getDescription = () => {
    if (isNotEnoughCover) {
      return (
        <Stack space={"medium"}>
          <Text tone={"neutralDark"}>
            {formatLabel(
              content.solarOutcome.descriptionNotEnoughCover,
              (value) => (
                <>{value}</>
              ),
              percentageDailyConsumption
            )}
          </Text>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.subDescriptionNotEnoughCover}
          </Text>
        </Stack>
      );
    } else if (isNoCover) {
      return (
        <Stack space={"medium"}>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.descriptionNotCover}
          </Text>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.subDescriptionNotCover}
          </Text>
        </Stack>
      );
    } else if (isOutlierCover) {
      return (
        <Stack space={"medium"}>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.descriptionOneHunderedCover}
          </Text>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.subDescriptionOutlier}
          </Text>
        </Stack>
      );
    } else {
      return (
        <Stack space={"medium"}>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.descriptionOneHunderedCover}
          </Text>
          <Text tone={"neutralDark"}>
            {content.solarOutcome.subDescriptionOneHunderedCover}
          </Text>
        </Stack>
      );
    }
  };

  return (
    <Box
      data-id={testId.solar}
      display="flex"
      marginX={"none"}
      marginY={{ xs: "medium", lg: "none" }}
      width="full"
    >
      <Stack space={"large"}>
        <StyledHeading tone="primaryB">
          {content.solarOutcome.title}
        </StyledHeading>

        <Inline space={"large"} alignY={"top"} disableWrap={true}>
          <IconSolar />

          <Stack space={"none"}>
            <Text variant={"subtitle-small"} tone={"neutralDark"}>
              <b>
                {formatLabel(
                  content.solarOutcome.heading,
                  (value) => (
                    <>{value}</>
                  ),
                  size || 0
                )}
              </b>
            </Text>

            {getDescription()}
          </Stack>
        </Inline>
        <SolarAccordion
          key={accordionId}
          accordionId={accordionId}
          defaultExpanded={false}
          solarBatterySystem={solarBatterySystem}
        />
      </Stack>
    </Box>
  );
});
